import { zodResolver } from '@hookform/resolvers/zod';
import { Box, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { dynamicInvoicePageSelectors, getSelectorAttribute } from '@vy/app-selectors';
import { getCurrencySymbol } from '@vy/services';
import { DEFAULT_CURRENCY, INTEGRATION_PROVIDERS, LINE_ITEM_TYPES } from '@vy/types';
import { isEmptyString, isNotNullOrUndefined, isNullOrUndefined } from '@vy/utils';
import { get } from 'lodash';
import type { PropsWithChildren } from 'react';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import type { QueryKey } from 'react-query';
import { vyFormRegister } from '../../../../../utils/react-hook-forms-utils';
import { useDynamicInvoice } from '../../dynamic-invoice-context';
import type { RevisionedLineItemDialogSubmitAction } from '../RevisionedLineItemDialogForm';
import {
  RevisionedLineItemDialogForm,
  RevisionedLineItemDialogSubmitActions,
} from '../RevisionedLineItemDialogForm';
import type { FixedRevisionedLineItemForm } from './revisioned-line-item-dialog-forms-types/fixed-revisioned-line-item-form';
import { FixedRevisionedLineItemFormSchema } from './revisioned-line-item-dialog-forms-types/fixed-revisioned-line-item-form';
import { NetSuiteClassesDropdown } from '../../../../plans/CreatePlanPage/create-plan-page-components/FixedChargeEditDrawer/fixed-charge-edit-drawer-components/components/NetSuiteClassesDropdown';
import { NetSuiteProductsDropdown } from '../../../../plans/CreatePlanPage/create-plan-page-components/FixedChargeEditDrawer/fixed-charge-edit-drawer-components/components/NetSuiteProductsDropdown';
import { XeroProductsDropdownGeneric } from '../../../../plans/CreatePlanPage/create-plan-page-components/FixedChargeEditDrawer/fixed-charge-edit-drawer-components/components/XeroProductsDropdownGeneric';
import { useActivatedIntegration } from '../../../../../hooks/use-activated-integration';

type AddFixedRevisionedLineItemDialogProps = PropsWithChildren<{
  lineItemId?: string;
  open: boolean;
  onClose?: () => void;
  invoiceId: string;
  defaultValues?: Partial<FixedRevisionedLineItemForm>;
  submitAction?: RevisionedLineItemDialogSubmitAction;
  queryKeyToInvalidate?: QueryKey;
}>;

export const FixedRevisionedLineItemDialogForm: React.FC<AddFixedRevisionedLineItemDialogProps> = ({
  lineItemId,
  open,
  onClose,
  invoiceId,
  defaultValues,
  submitAction,
  queryKeyToInvalidate,
}) => {
  const isNetSuiteEnabled = useActivatedIntegration(INTEGRATION_PROVIDERS.netSuite, { suspense: true });
  const isXeroEnabled = useActivatedIntegration(INTEGRATION_PROVIDERS.xero, { suspense: true });
  const form = useForm<FixedRevisionedLineItemForm>({
    resolver: zodResolver(FixedRevisionedLineItemFormSchema),
    defaultValues,
  });

  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues]);

  const fixedRevisionedLineItemNameRegister = vyFormRegister(form.register, 'modifiedName');
  const fixedRevisionedLineItemDescriptionRegister = vyFormRegister(form.register, 'modifiedDescription');
  const fixedRevisionedLineItemPriceRegister = form.register('revenueBreakdown.total', {
    valueAsNumber: true,
  });

  const title =
    submitAction === RevisionedLineItemDialogSubmitActions.Create
      ? 'Add product or service fee to this invoice'
      : 'Update product or service fee in this invoice';
  const submitButtonLabel =
    submitAction === RevisionedLineItemDialogSubmitActions.Create ? 'Add item' : 'Update';

  const { dynamicInvoiceData } = useDynamicInvoice();

  const currency = dynamicInvoiceData.customer.settings?.currency ?? DEFAULT_CURRENCY;

  return (
    <RevisionedLineItemDialogForm
      invoiceId={invoiceId}
      lineItemId={lineItemId}
      lineItemType={LINE_ITEM_TYPES.Fixed}
      open={open}
      onClose={onClose}
      form={form}
      submitAction={submitAction}
      title={title}
      submitButtonLabel={submitButtonLabel}
      queryKeyToInvalidate={queryKeyToInvalidate}
    >
      <TextField
        {...getSelectorAttribute(dynamicInvoicePageSelectors.revision.fixedRevisionModal.itemNameField)}
        {...fixedRevisionedLineItemNameRegister}
        label="Item Name"
        placeholder="Type Name Here..."
        size="medium"
        fullWidth
        sx={{ padding: '10px' }}
        error={isNotNullOrUndefined(form.formState.errors[fixedRevisionedLineItemNameRegister.name])}
        helperText={form.formState.errors[fixedRevisionedLineItemNameRegister.name]?.message}
      />
      <Stack
        direction="row"
        sx={{
          gap: 3,
          padding: "10px",
          alignItems: "flex-start"
        }}>
        <Box sx={{
          flex: "1 1 0"
        }}>
          <TextField
            {...getSelectorAttribute(
              dynamicInvoicePageSelectors.revision.fixedRevisionModal.descriptionField,
            )}
            {...fixedRevisionedLineItemDescriptionRegister}
            label="Description"
            placeholder="Type Description Here..."
            size="medium"
            fullWidth
            error={isNotNullOrUndefined(
              form.formState.errors[fixedRevisionedLineItemDescriptionRegister.name],
            )}
            helperText={form.formState.errors[fixedRevisionedLineItemDescriptionRegister.name]?.message}
          />
        </Box>

        <Box sx={{
          flex: "1 1 0"
        }}>
          <TextField
            {...fixedRevisionedLineItemPriceRegister}
            {...getSelectorAttribute(dynamicInvoicePageSelectors.revision.fixedRevisionModal.priceField)}
            label="Amount"
            placeholder="0.00"
            size="medium"
            fullWidth
            type="number"
            onBlur={(event) => {
              if (isEmptyString(event.target.value)) {
                form.setValue(fixedRevisionedLineItemPriceRegister.name, 0);
              }
            }}
            error={isNotNullOrUndefined(
              get(form.formState.errors, fixedRevisionedLineItemPriceRegister.name),
            )}
            helperText={get(form.formState.errors, fixedRevisionedLineItemPriceRegister.name)?.message}
            slotProps={{
              input: {
                startAdornment: <InputAdornment position="start">{getCurrencySymbol(currency)}</InputAdornment>,
              }
            }}
          />
          <Typography
            variant="body2"
            sx={{
              fontSize: "12px",
              color: "text.secondary",
              marginTop: "11px"
            }}>
            Not including tax. Taxes will be applied according to your account settings.
          </Typography>
        </Box>
      </Stack>
      {(isNetSuiteEnabled || isXeroEnabled) && (
        <Stack
          sx={{
            gap: 2,
            padding: 1
          }}>
          <Typography
            sx={{
              color: "text.secondary",
              fontSize: 13
            }}>
            ERP Settings
          </Typography>

          {isNetSuiteEnabled && (
            <>
              <NetSuiteProductsDropdown
                fieldPath="netSuiteProduct"
                control={form.control}
                showDropdown
                customerId={dynamicInvoiceData.customer.id}
                onChange={(modifiedIntegrationEntity) => {
                  if (isNullOrUndefined(modifiedIntegrationEntity)) {
                    form.setValue('classMetadata', null);
                  }
                }}
              />
              <NetSuiteClassesDropdown control={form.control} fieldPath="classMetadata" />
            </>
          )}

          {isXeroEnabled && (
            <XeroProductsDropdownGeneric fieldPath="xeroProductId" control={form.control} showDropdown />
          )}
        </Stack>
      )}
    </RevisionedLineItemDialogForm>
  );
};
